import React from 'react';

/**
 * FormField Component
 * 
 * Wrapper component for form fields with consistent layout.
 * Handles label, error, and helper text positioning.
 * Works with react-hook-form integration.
 * 
 * @example
 * ```tsx
 * <FormField
 *   label="Email"
 *   error={errors.email?.message}
 *   required
 * >
 *   <input {...register('email')} />
 * </FormField>
 * ```
 */

export interface FormFieldProps {
  /** Field label */
  label?: string;
  /** Error message from validation */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Mark field as required */
  required?: boolean;
  /** Children (form control) */
  children: React.ReactNode;
  /** HTML for attribute (connects label to input) */
  htmlFor?: string;
  /** Container className */
  className?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  error,
  helperText,
  required = false,
  children,
  htmlFor,
  className = '',
}) => {
  return (
    <div className={`w-full ${className}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={htmlFor}
          className="block text-sm font-medium text-gray-900 mb-1.5 dark:text-dark-text"
        >
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      {/* Form Control */}
      <div className="w-full">{children}</div>

      {/* Helper Text or Error */}
      {(helperText || error) && (
        <p
          className={`mt-1.5 text-sm ${
            error
              ? 'text-error-600 dark:text-error-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
};

FormField.displayName = 'FormField';

export default FormField;
